import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WellbeingData, CategoryStatus } from '../../types/wellbeing';
import { MoodIcon, GutIcon, JointIcon, FoodIcon, NotesIcon } from './CategoryIcons';
import { MoodModal } from './modals/MoodModal';
import { GutSymptomsModal } from './modals/GutSymptomsModal';
import { ExtraIntestinalModal } from './modals/ExtraIntestinalModal';
import { FoodModal } from './modals/FoodModal';
import { OtherModal } from './modals/OtherModal';
import { saveWellbeingEntry, updateWellbeingEntry, getWellbeingEntryByDate } from '../../lib/wellbeingService';

type ModalType = 'mood' | 'gut' | 'extra' | 'food' | 'other' | null;

export const AddWellbeing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<WellbeingData>({
    date: new Date().toISOString().split('T')[0],
  });

  const loadEntryForDate = useCallback(async (date: string) => {
    try {
      setLoading(true);

      const entry = await getWellbeingEntryByDate(date);

      if (entry) {
        setEditingEntryId(entry.id);
        setFormData({
          date: entry.date,
          mood: entry.mood,
          stress: entry.stress,
          stressNote: entry.stressNote,
          energy: entry.energy,
          sleepHours: entry.sleepHours,
          sleepQuality: entry.sleepQuality,
          abdominalPain: entry.abdominalPain,
          bloating: entry.bloating,
          rumbling: entry.rumbling,
          heartburn: entry.heartburn,
          nausea: entry.nausea,
          vomiting: entry.vomiting,
          lossOfAppetite: entry.lossOfAppetite,
          fullness: entry.fullness,
          jointPain: entry.jointPain,
          erythemaNodosum: entry.erythemaNodosum,
          pyodermaGangrenosum: entry.pyodermaGangrenosum,
          skinOther: entry.skinOther,
          skinOtherNote: entry.skinOtherNote,
          eyeProblems: entry.eyeProblems,
          mouthSores: entry.mouthSores,
          dietChange: entry.dietChange,
          dietChangeNote: entry.dietChangeNote,
          mealHeaviness: entry.mealHeaviness,
          alcohol: entry.alcohol,
          alcoholNote: entry.alcoholNote,
          physicalActivity: entry.physicalActivity,
          weight: entry.weight,
          fever: entry.fever,
          temperature: entry.temperature,
          notes: entry.notes,
        });
      } else {
        setEditingEntryId(null);
        setFormData({ date });
      }
    } catch (error) {
      console.error('Error loading entry:', error);
      toast.error('Nie udało się załadować wpisu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const dateFromUrl = searchParams.get('date');
    const targetDate = dateFromUrl || new Date().toISOString().split('T')[0];

    setFormData(prev => ({ ...prev, date: targetDate }));
    loadEntryForDate(targetDate);
  }, [searchParams, loadEntryForDate]);

  const getCategoryStatus = (category: string): CategoryStatus => {
    switch (category) {
      case 'mood': {
        const fields = [formData.mood, formData.stress, formData.energy, formData.sleepHours, formData.sleepQuality];
        const filled = fields.filter(f => f !== undefined && f !== 0).length;
        if (filled === 0) return 'empty';
        if (filled === fields.length) return 'complete';
        return 'partial';
      }
      case 'gut': {
        const fields = [formData.abdominalPain, formData.bloating, formData.rumbling, formData.heartburn, formData.nausea, formData.vomiting, formData.lossOfAppetite, formData.fullness];
        const filled = fields.filter(f => f !== undefined && f !== 0).length;
        if (filled === 0) return 'empty';
        if (filled === fields.length) return 'complete';
        return 'partial';
      }
      case 'extra': {
        // Pola z pozytywnymi danymi (user coś zaznaczył lub wpisał)
        const hasPositive =
          (formData.jointPain ?? 0) > 0 ||
          formData.erythemaNodosum ||
          formData.pyodermaGangrenosum ||
          (formData.skinOther === true && !!formData.skinOtherNote?.trim()) ||
          formData.eyeProblems ||
          formData.mouthSores;
        if (!hasPositive) return 'empty';
        // Wszystkie 6 uzupełnione (jointPain to liczba 0–5, skinOther: false lub true+z opisem)
        const skinAnswered = formData.skinOther === false || (formData.skinOther === true && !!formData.skinOtherNote?.trim());
        const allAnswered =
          formData.jointPain !== undefined &&
          formData.erythemaNodosum !== undefined &&
          formData.pyodermaGangrenosum !== undefined &&
          skinAnswered &&
          formData.eyeProblems !== undefined &&
          formData.mouthSores !== undefined;
        if (allAnswered) return 'complete';
        return 'partial';
      }
      case 'food': {
        const fields = [formData.dietChange, formData.mealHeaviness, formData.alcohol];
        const filled = fields.filter(f => f !== undefined && f !== false && f !== 0).length;
        if (filled === 0) return 'empty';
        if (filled === 3) return 'complete';
        return 'partial';
      }
      case 'other': {
        const fields = [formData.physicalActivity, formData.weight, formData.fever, formData.notes];
        const filled = fields.filter(f => f !== undefined && f !== false && f !== 0 && f !== '').length;
        if (filled === 0) return 'empty';
        if (filled === 4) return 'complete';
        return 'partial';
      }
      default:
        return 'empty';
    }
  };

  const handleSave = async () => {
    try {
      const loadingToast = toast.loading('Zapisywanie...');

      if (editingEntryId) {
        await updateWellbeingEntry(editingEntryId, formData);
        toast.success('Wpis zaktualizowany', { id: loadingToast });
      } else {
        const newEntry = await saveWellbeingEntry(formData);
        setEditingEntryId(newEntry.id);
        toast.success('Wpis zapisany', { id: loadingToast });
      }

      await loadEntryForDate(formData.date);
    } catch (error) {
      console.error('Error saving wellbeing:', error);
      const msg = error instanceof Error ? error.message : 'Nie udało się zapisać';
      toast.error(msg);
    }
  };

  const StatusIcon = ({ status }: { status: CategoryStatus }) => {
    if (status === 'complete') {
      return (
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: '#10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      );
    }
    if (status === 'partial') {
      return (
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: 'white',
          }} />
        </div>
      );
    }
    return null;
  };

  const CategoryButton = ({
    IconComponent,
    title,
    description,
    category,
    onClick
  }: {
    IconComponent: React.FC;
    title: string;
    description: string;
    category: string;
    onClick: () => void;
  }) => {
    const status = getCategoryStatus(category);

    return (
      <button
        onClick={onClick}
        style={{
          width: '100%',
          padding: '16px',
          borderRadius: '16px',
          border: '2px solid #e2e8f0',
          background: 'white',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#cbd5e1';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <IconComponent />
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#0f172a',
            margin: '0 0 4px',
          }}>
            {title}
          </h3>
          <p style={{
            fontSize: '13px',
            color: '#64748b',
            margin: 0,
          }}>
            {description}
          </p>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <StatusIcon status={status} />

          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </button>
    );
  };

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#0f172a',
              margin: 0,
            }}>
              Samopoczucie
            </h1>

            <button
              onClick={() => navigate('/wellbeing/history')}
              style={{
                padding: '12px 20px',
                borderRadius: '10px',
                border: '2px solid #6B7FFF',
                background: 'white',
                color: '#6B7FFF',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Historia wpisów
            </button>
          </div>

        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#64748b',
          }}>
            Ładowanie wpisu...
          </div>
        ) : (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              <CategoryButton
                IconComponent={MoodIcon}
                title="Samopoczucie"
                description="Nastrój, energia, stres, sen"
                category="mood"
                onClick={() => setActiveModal('mood')}
              />

              <CategoryButton
                IconComponent={GutIcon}
                title="Objawy jelitowe"
                description="Ból, wzdęcia, zgaga, nudności..."
                category="gut"
                onClick={() => setActiveModal('gut')}
              />

              <CategoryButton
                IconComponent={JointIcon}
                title="Objawy pozajelitowe"
                description="Stawy, skóra, oczy, afty"
                category="extra"
                onClick={() => setActiveModal('extra')}
              />

              <CategoryButton
                IconComponent={FoodIcon}
                title="Jedzenie"
                description="Dieta, ciężkość posiłków, alkohol"
                category="food"
                onClick={() => setActiveModal('food')}
              />

              <CategoryButton
                IconComponent={NotesIcon}
                title="Inne"
                description="Aktywność, waga, gorączka, notatki"
                category="other"
                onClick={() => setActiveModal('other')}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                width: '100%',
                marginTop: '24px',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: loading
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(107, 127, 255, 0.3)',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Ładowanie...' : 'Zapisz samopoczucie'}
            </button>
          </>
        )}
      </div>

      {activeModal === 'mood' && (
        <MoodModal
          data={formData}
          onSave={(data) => {
            setFormData(prev => ({ ...prev, ...data }));
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'gut' && (
        <GutSymptomsModal
          data={formData}
          onSave={(data) => {
            setFormData(prev => ({ ...prev, ...data }));
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'extra' && (
        <ExtraIntestinalModal
          data={formData}
          onSave={(data) => {
            setFormData(prev => ({ ...prev, ...data }));
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'food' && (
        <FoodModal
          data={formData}
          onSave={(data) => {
            setFormData(prev => ({ ...prev, ...data }));
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'other' && (
        <OtherModal
          data={formData}
          onSave={(data) => {
            setFormData(prev => ({ ...prev, ...data }));
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
};
